import type { OnDestroy, Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import type {
  CustomFieldDefinition,
  CustomFieldEntityType
} from '@awork/features/project/models/custom-field-definition.model'
import { CustomFieldDefinitionStore } from '@awork/features/project/state/custom-field-definition.store'
import { Order } from '@awork/core/state/signal-store/types'
import { CustomFieldTypeValue } from '@awork/features/project/models/custom-field-type.model'
import { CustomFieldDefinitionLink } from '@awork/features/project/models/custom-field-definition-link.model'

@Injectable({ providedIn: 'root' })
export class CustomFieldDefinitionQuery extends EntitySignalQuery<CustomFieldDefinition> implements OnDestroy {
  protected store: CustomFieldDefinitionStore

  static instance: CustomFieldDefinitionQuery // Used to query the store in static methods

  constructor() {
    const store = inject(CustomFieldDefinitionStore)

    super(store)
    this.store = store

    CustomFieldDefinitionQuery.instance = this
  }

  /**
   * Clears the static reference when Angular destroys this query.
   */
  ngOnDestroy(): void {
    if (CustomFieldDefinitionQuery.instance === this) {
      Reflect.deleteProperty(CustomFieldDefinitionQuery, 'instance')
    }
  }

  /**
   * Gets whether the complete custom field definition list has loaded.
   * @returns {Signal<boolean>}
   */
  queryHasLoadedAllDefinitions(): Signal<boolean> {
    return this.store.getHasLoadedAllDefinitions()
  }

  /**
   * Gets all custom field definitions
   * @param {CustomFieldEntityType} entity
   * @param {boolean} isExternal
   * @param {keyof CustomFieldDefinition} sortBy
   * @returns {Signal<CustomFieldDefinition[]>}
   */
  queryCustomFieldDefinitions(
    entity?: CustomFieldEntityType,
    isExternal?: boolean,
    sortBy?: keyof CustomFieldDefinition
  ): Signal<CustomFieldDefinition[]> {
    return computed(() => {
      const customFieldDefinitions = this.queryAll({
        filterBy: customFieldDefinition =>
          (!entity || customFieldDefinition.entity === entity) &&
          (isExternal ? customFieldDefinition.isExternal : !customFieldDefinition.isExternal),
        sortBy: sortBy || 'name',
        sortByOrder: Order.ASC
      })

      // Order selection options by order
      customFieldDefinitions()
        .filter(
          customFieldDefinition =>
            customFieldDefinition.type === CustomFieldTypeValue.Select ||
            customFieldDefinition.type === CustomFieldTypeValue.ColoredSelect
        )
        .forEach(customFieldDefinition => {
          customFieldDefinition.selectionOptions = customFieldDefinition.selectionOptions.sort(
            (a, b) => a.order - b.order
          )
        })

      return customFieldDefinitions()
    })
  }

  /**
   * Gets a custom field definition by id
   * @param {string} id
   * @returns {Signal<CustomFieldDefinition>}
   */
  queryCustomFieldDefinition(id: string): Signal<CustomFieldDefinition> {
    return computed(() => {
      const customFieldDefinition = this.queryEntity(id)

      // Order selection options by order
      if (
        customFieldDefinition() &&
        (customFieldDefinition().type === CustomFieldTypeValue.Select ||
          customFieldDefinition().type === CustomFieldTypeValue.ColoredSelect)
      ) {
        customFieldDefinition().selectionOptions = customFieldDefinition().selectionOptions.sort(
          (a, b) => a.order - b.order
        )
      }

      return customFieldDefinition()
    })
  }

  /**
   * Gets task custom field definitions linked to any of the provided project ids.
   * @param {Signal<string[]>} projectIds
   * @returns {Signal<CustomFieldDefinition[]>}
   * @withSignal {string[]} projectIds
   */
  queryCustomFieldDefinitionsWithLinkedProjectIds(projectIds: Signal<string[]>): Signal<CustomFieldDefinition[]> {
    const taskCustomFieldDefinitions = this.queryCustomFieldDefinitions('task')

    return computed(() => {
      const projectIdsSet = new Set(projectIds())

      if (!projectIdsSet.size) {
        return []
      }

      return taskCustomFieldDefinitions().filter(customFieldDefinition =>
        customFieldDefinition.linkedProjectIds?.some(projectId => projectIdsSet.has(projectId))
      )
    })
  }

  /**
   * Gets the custom field definitions linked to a project
   * @param {string} projectId
   * @returns {Signal<CustomFieldDefinitionLink[]>}
   */
  queryLinkedCustomFieldDefinitions(projectId: string): Signal<CustomFieldDefinitionLink[]> {
    return computed(() => {
      const customFieldDefinitions = this.queryAll({
        sortBy: 'name',
        sortByOrder: Order.ASC
      })

      return customFieldDefinitions()
        .filter(customFieldDefinition => customFieldDefinition.linkedProjectIds?.includes(projectId))
        .map(customFieldDefinition => new CustomFieldDefinitionLink({ ...customFieldDefinition, projectId, order: 0 }))
    })
  }
}
